import * as GameTypes from 'types/game.types';

type HEX = `#${string}`;
interface BaseTheme {
    name: string,
    credits?: number,
    menuBg: Partial<CSSStyleDeclaration>,
    gameBg: HEX,
    /**
     * The palette should be an abbreviated set of colors which can be used to give an
     * impression of the theme. These values shouldn't be used for anything other than previews.
     */
    palette: HEX[],
    /**
     * The theme values used throughout the UI and game. The theme store provides some helpful methods for
     * retrieving theme colors in different formats.
     */
    values: {
        [key:string]: HEX
    },
    /**
     * Theme color sets used throughout the UI and game. The theme store provides some helpful methods for
     * accessing set colors.
     */
    sets: {
        [key:string]: HEX[]
    }
}

type Theme = BaseTheme & {
    titleBg: string,
    fontColor: HEX,
    cardBg: HEX,
    values: {},
    sets: {
        backgroundFX: HEX[]
    }
}

type ThemeKey = 'default' | 'hotpink' | 'tropical' | 'interstellar' | 'ultraviolet' | 'cottoncandy' | 'garden' | 'glacier';
const Themes : { [key in ThemeKey]: Theme } = {
    default: {
        name: "Default",
        palette: ['#A748FA'],
        titleBg: 'linear-gradient(90deg, #00FFF0 0%, #EB00FF 100%)',
        fontColor: '#ffffff',
        cardBg: '#0000006c',
        menuBg: {
            background: '#14171e'
        },
        gameBg: '#14171e',
        values: {},
        sets: {
            backgroundFX: [
                '#08f7f1',
                '#58a0f6',
                '#8371f8',
                '#e704ff'
            ]
        }
    },
    interstellar: {
        name: "Interstellar",
        palette: ['#000000'],
        titleBg: 'linear-gradient(90deg, #FFFFFF 0%, #4E4E4E 100%)',
        fontColor: '#ffffff',
        cardBg: '#ffffff24',
        menuBg: {
            background: '#000000'
        },
        gameBg: '#000000',
        values: {},
        sets: {
            backgroundFX: [
                "#ffffff"
            ]
        }
    },
    hotpink: {
        name: "Hot Pink",
        palette: ['#FF00E5'],
        titleBg: 'linear-gradient(90deg, #FF4D00 0%, #EB00FF 100%)',
        fontColor: '#FF00E5',
        cardBg: '#0000006c',
        menuBg: {
            background: '#1c1321'
        },
        gameBg: '#291E30',
        values: {},
        sets: {
            backgroundFX: [
                "#FF00E5"
            ]
        }
    },
    tropical: {
        name: "Tropical",
        palette: ['#00FF38'],
        titleBg: 'linear-gradient(90deg, #008CB8 0%, #00FF29 100%)',
        fontColor: '#00FF38',
        cardBg: '#0000006c',
        menuBg: {
            background: '#131a13',
        },
        gameBg: '#182417',
        values: {},
        sets: {
            backgroundFX: [
                "#00DD31",
                "#DDBA00",
                "#00B5DD"
            ]
        }
    },
    ultraviolet: {
        name: "Ultraviolet",
        palette: ['#110043'],
        titleBg: 'linear-gradient(90deg, #ff00f7 0%, #3500ff 100%)',
        fontColor: '#00FF38',
        cardBg: '#0000006c',
        menuBg: {
            background: 'linear-gradient(169.02deg, rgb(28, 19, 33) -0.18%, #110043 113.57%)',
        },
        gameBg: '#200043',
        values: {},
        sets: {
            backgroundFX: [
                "#FF00E5"
            ]
        }
    },
    cottoncandy: {
        name: "Cotton Candy",
        palette: ['#99cff4'],
        titleBg: 'linear-gradient(90deg, #ff00f7 0%, #00ffbb 100%)',
        fontColor: '#00FF38',
        cardBg: '#0000006c',
        menuBg: {
            background: 'linear-gradient(169.02deg, #443F69 -0.18%, #2a2837 113.57%)',
            //backgroundSize: '100% 100%'
        },
        gameBg: '#443f69',
        values: {},
        sets: {
            backgroundFX: [
                '#99cff4'
            ]
        }
    },
    garden: {
        name: "Earthtone",
        palette: ['#ADE6B0'],
        titleBg: 'linear-gradient(90deg, #ff00f7 0%, #ffca00 100%)',
        fontColor: '#00FF38',
        cardBg: '#0000006c',
        menuBg: {
            background: '#291717',
            //backgroundSize: '100% 100%'
        },
        gameBg: '#291717',
        values: {},
        sets: {
            backgroundFX: [
                '#ce00ff',
                '#0fa100',
                '#ADE6B0', 
                '#FEDC78', 
                '#F6F5AD', 
                '#CFEEB7'
            ]
        }
    },
    glacier: {
        name: "Glacier",
        palette: ['#66B3CC'],
        titleBg: 'linear-gradient(90deg, #ffffff 0%, #43a7ff 100%)',
        fontColor: '#00FF38',
        cardBg: '#0000006c',
        menuBg: {
            background: '#003D4E',
            //backgroundSize: '100% 100%'
        },
        gameBg: '#002935',
        values: {},
        sets: {
            backgroundFX: [
                '#66B3CC', 
                '#ffffff', 
                '#94e5ff'
            ]
        }
    },
}

export {
    HEX,
    Theme,
    Themes,
    ThemeKey
};
